import React, { useEffect, useMemo, useState } from "react";
import Table from "../../components/utils/Table";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import Button from "../../components/utils/Button";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import CardLayout from "../../components/utils/CardLayout";
import purchaseSimActions from "../../redux/inventory/purchaseSim/actions";
import genericActions from "../../redux/generic/actions";
import moment from "moment";

const { getPurchaseSimList } = purchaseSimActions;
const { getACode } = genericActions;
const dateFormat = process.env.REACT_APP_DATE_TIME_FORMAT;


const PurchaseSimList = () => {
  const dispatch = useDispatch();
  const today = new Date();
  const twoDaysBefore = new Date();
  twoDaysBefore.setDate(today.getDate() - 2);
  const formatDate = (date) => date.toISOString().split("T")[0];
  const {
    purchaseSimList,
    getPurchaseSimListLoading,
    getPurchaseSimListError,
  } = useSelector((state) => state.purchaseSim);
  const { aCodeList } = useSelector((state) => state.generic);

  const [searchItem, setSearchItem] = useState("");
  const defaultFromDate = formatDate(twoDaysBefore);
  const defaultToDate = formatDate(today);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
          distributorId: "0",
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    },
  });

  const filteredItems = useMemo(() => {
    return Array.isArray(purchaseSimList) && purchaseSimList.length > 0
      ? purchaseSimList.filter((item) => {
        if (!searchItem) return true;
        return Object.values(item).some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(searchItem.toLowerCase()),
        );
      })
      : [];
  }, [purchaseSimList, searchItem]);

  const dataSource = filteredItems.map((item, key) => ({
    key: key + 1,
    distributorAcode: item?.DistributorAcode,
    purchaseDate: moment(item.PurchaseDate).format(dateFormat),
    purchaseNumber: item?.PurchaseNumber,
    SIMType: item?.SIMType,
    totalSIMCount: item?.TotalSIMCount,
  }));

  const column = [
    { displayName: "Acode", key: "distributorAcode" },
    { displayName: "Purchase Number", key: "purchaseNumber" },
    { displayName: "Purchase Date", key: "purchaseDate" },
    { displayName: "Type", key: "SIMType" },
    { displayName: "Total SIM Count", key: "totalSIMCount" },
  ];

  useEffect(() => {
    dispatch(
      getPurchaseSimList({ fromDate: defaultFromDate, toDate: defaultToDate, }),
    );
    dispatch(getACode());
  }, []);


  const handleSearch = (data) => {
    data.distributorId = Number(data.distributorId || 0)
    dispatch(getPurchaseSimList(data));
  };

  const downloadExcelFile = () => {
    const data = dataSource.map((item, key) => (
      {
        "ACode": item?.distributorAcode,
        "Purchase Number": item?.purchaseNumber,
        "Purchase Date": moment(item?.purchaseDate).format(dateFormat),
        "Type": item?.SIMType,
        "Total SIM Count": item?.totalSIMCount,
      }
    ))
    downloadExcelSampleFile(data, "purchase-sim-list.xlsx");
  }
  return (
    <CardLayout title=" Purchase SIM List" icon="ri-sim-card-2-line"
      rightContent={
          <div className="d-flex flex-wrap align-items-center gap-1">
              <input
            type="search"
            style={{ width: "300px", }}
            className="form-control"
            placeholder="Search..."
            onChange={(e) => setSearchItem(e.target.value)}
          />
        <Button
          title="Excel"
          buttonType="button"
          variant="success"
          icon={<i className="ri-download-cloud-line me-2"></i>}
          onClick={downloadExcelFile}
         />
        </div>
      }>
      <div className="row">
        {/* Search From*/}
        <form onSubmit={handleSubmit(handleSearch)} className="col-lg-10 col-md-12 ">
          <div className="row">
            {/* ACode */}
            <div className="form-group col-lg-2 col-md-4 col-12 col-sm-6">
              <label className="form-label fw-semibold">
                ACode
              </label>

              <select
                className={`form-select`}
                {...register("distributorId",)}
              >
                <option value="0">All</option>
                {aCodeList?.map((item) => (
                  <option key={item?.Id} value={item?.Id}>
                    {item?.ACode}
                  </option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div className="col-lg-3 col-md-4 mb-3">
              <label className="form-label fw-semibold">
                {" "}
                From Date <span className="text-danger">*</span>{" "}
              </label>

              <input
                type="date"
                className={`form-control ${errors?.fromDate ? "is-invalid" : ""}`}
                {...register("fromDate", {
                  required: "From date is required",
                })}
              />

              {errors?.fromDate && (
                <div className="invalid-feedback">
                  {" "}
                  {errors?.fromDate?.message}{" "}
                </div>
              )}
            </div>

            {/* To Date */}
            <div className="col-lg-3 col-md-4 mb-3">
              <label className="form-label fw-semibold">
                {" "}
                To Date <span className="text-danger">*</span>{" "}
              </label>

              <input
                type="date"
                className={`form-control ${errors?.toDate ? "is-invalid" : ""}`}
                {...register("toDate", { required: "To date is required" })}
              />

              {errors?.toDate && (
                <div className="invalid-feedback"> {errors.toDate.message} </div>
              )}
            </div>

            {/* Button */}
            <div className="col-lg-3 col-md-4 mt-md-3 d-flex align-items-center">
              <div className="d-flex gap-2 w-100">
                <Button
                  title="Get Report"
                  buttonType="submit"
                  variant="primary"
                  icon={<i className="ri-search-line me-2"></i>}
                  loading={getPurchaseSimListLoading}
                  disable={getPurchaseSimListLoading}
                />
              </div>
            </div>
          </div>
        </form>
      
      </div>
      {/* Table */}
      <div className="mt-2">
        <Table
          columns={column}
          loading={getPurchaseSimListLoading}
          data={dataSource}
          pagination={true}
          tableHeight="320px"
        />
      </div>
    </CardLayout>
  );
};

export default PurchaseSimList;
